import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { rciApi } from "../api/rci";
import { journalEntriesApi } from "../api/journalEntries";
import { daySignalLabel } from "../utils/dailySignalLabel";
import { useBackOrFallback } from "../hooks/useBackOrFallback";
// Shared with Journal.jsx, which renders the same reverse-chronological
// "Week of …" layout over a different data source. Extracted from this
// file; the timezone-safe local-date parsing is the part that must not
// exist in two copies.
import {
  localDateKey as _localDateKey,
  fullDayLabel as _fullDayLabel,
  mondayOf as _mondayOf,
  weekHeader as _weekHeader,
} from "../utils/weekGrouping";

// Signal Timeline feed — /history.
//
// Renders the user's RCISnapshot history grouped by week (Monday-
// anchored). Today's card is dark-themed; older cards are white. The
// per-card label is derived from the numeric history via
// daySignalLabel, NOT from the (single, non-historical)
// insights.current_insight column.
//
// Pagination: initial load fetches 28 days; an in-viewport sentinel
// at the bottom triggers another 28 with ``before=<oldest_window_end>``
// until the server returns an empty page.
//
// A second, additive lookup (GET /journal-entries/dates) marks which of
// the loaded days have free-write entries behind them, so the journal
// connection is visible on the LIST rather than only after drilling
// into a day. Existence only — dates and counts, never the writing.

const PAGE_DAYS = 28;
const VELLUM = "#F2EDE3";
const ANTIQUE_GOLD = "#9E8456";
const WARM_BLACK = "#1C1814";
const TEXT_SECONDARY = "rgba(28,24,20,0.62)";
const BORDER = "rgba(28,24,20,0.10)";
const SERIF = "'Playfair Display', serif";

function DayCard({ snapshot, idx, snapshotsAll, isToday, onTap, hasJournal }) {
  const score = Math.max(1, Math.round((snapshot.rci_total || 0) * 100));
  const label = daySignalLabel(idx, snapshotsAll);
  const dateLine = _fullDayLabel(snapshot.window_end);

  // Dark card for today, white for everything else. Both share the
  // 12px radius + same internal spacing so the visual weight reads
  // as "highlighted" rather than "different kind of card".
  const isDark = isToday;
  const bg = isDark ? WARM_BLACK : "white";
  const titleColor = isDark ? VELLUM : WARM_BLACK;
  const scoreColor = ANTIQUE_GOLD;
  const labelColor = isDark ? ANTIQUE_GOLD : TEXT_SECONDARY;
  const border = isDark ? "none" : `0.5px solid ${BORDER}`;

  return (
    <button
      type="button"
      onClick={() => onTap(snapshot.window_end)}
      style={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        background: bg,
        borderRadius: 12,
        padding: "12px 14px",
        border,
        boxSizing: "border-box",
        width: "100%",
      }}
      aria-label={`Open ${dateLine}`}
    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 6,
      }}>
        <span style={{
          fontSize: 13, color: titleColor, fontWeight: 500,
          display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          {dateLine}
          {/* A day with free-write behind it. The connection used to be
              invisible until you opened the day; this is that fact, one
              level up. A dot and nothing more — the writing is excluded
              from the score beside it, so anything louder would start
              implying the two are related. */}
          {hasJournal && (
            <span
              role="img"
              aria-label="has journal entries"
              title="Journal entries on this day"
              style={{
                width: 5, height: 5, borderRadius: "50%",
                background: ANTIQUE_GOLD, flexShrink: 0,
              }}
            />
          )}
        </span>
        <span style={{
          fontSize: 20, color: scoreColor, fontFamily: SERIF, lineHeight: 1,
        }}>
          {score}
        </span>
      </div>
      <p style={{
        fontSize: 12, color: labelColor, margin: 0, lineHeight: 1.4,
      }}>
        {label}
      </p>
    </button>
  );
}


export default function SignalHistory() {
  const navigate = useNavigate();
  // Deep-link safe back: when the user lands on /history directly
  // (Dashboard dark card → first SPA tap; notification deep link;
  // pasted URL), there's nothing to pop — fall back to /.
  const goBack = useBackOrFallback("/");
  const { user, token } = useAuth();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  // Dates (YYYY-MM-DD) in the loaded window that have free-write
  // entries. Existence only — the endpoint returns dates and counts, no
  // bodies. Additive: an empty set just means no dots.
  const [journalDates, setJournalDates] = useState(() => new Set());
  const sentinelRef = useRef(null);

  const today = useMemo(() => _localDateKey(), []);

  // Initial load — 28 days, no ``before``.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    rciApi.history(user.id, PAGE_DAYS, token).then(rows => {
      if (cancelled) return;
      // API returns most-recent-first. Keep that order in state so
      // the visible list reads top-to-bottom newest-to-oldest, which
      // matches the mockup.
      const arr = Array.isArray(rows) ? rows : [];
      setSnapshots(arr);
      setHasMore(arr.length === PAGE_DAYS);
    }).catch(() => {
      if (!cancelled) setError("Couldn't load your signal history.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user, token]);

  // Paginate when sentinel comes into view.
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || !snapshots.length) return;
    const oldest = snapshots[snapshots.length - 1];
    if (!oldest?.window_end) return;
    setLoadingMore(true);
    rciApi.history(user.id, PAGE_DAYS, token, oldest.window_end).then(rows => {
      const arr = Array.isArray(rows) ? rows : [];
      setSnapshots(prev => [...prev, ...arr]);
      setHasMore(arr.length === PAGE_DAYS);
    }).catch(() => {
      // Soft-fail the paginate — the cards already on screen stay.
      setHasMore(false);
    }).finally(() => setLoadingMore(false));
  }, [user, token, snapshots, hasMore, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) loadMore(); }),
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  // Which of the loaded days have free-write entries. Keyed on the span
  // actually on screen (oldest…newest window_end), so paging in another
  // 28 days re-asks once for the widened span rather than per card.
  // Soft-fails: no dots is the correct degraded state for a marker.
  const oldestDate = snapshots.length ? snapshots[snapshots.length - 1].window_end : null;
  const newestDate = snapshots.length ? snapshots[0].window_end : null;
  useEffect(() => {
    if (!token || !oldestDate || !newestDate) return;
    let cancelled = false;
    journalEntriesApi.datesInRange(oldestDate, newestDate, token).then((res) => {
      if (cancelled) return;
      const rows = Array.isArray(res?.dates) ? res.dates : [];
      setJournalDates(new Set(rows.map((r) => r.date)));
    }).catch(() => { /* no markers rather than a broken timeline */ });
    return () => { cancelled = true; };
  }, [token, oldestDate, newestDate]);

  // Chronological (oldest→newest) view of the loaded snapshots, for
  // feeding daySignalLabel. ``snapshots`` itself stays newest-first —
  // that's the display order — but daySignalLabel treats ``idx - 1``
  // as "yesterday" (day-over-day deltas, streak detection), which
  // only holds with a chronological array. Passing the raw
  // newest-first array + its own index (the previous bug) made those
  // rules compare each day against the NEXT more recent day instead
  // of the prior one — backwards. DayDetail.jsx already reverses
  // before calling the same functions; this mirrors that pattern.
  const chronoSnapshots = useMemo(() => [...snapshots].reverse(), [snapshots]);

  // Group snapshots by Monday-of-week. ``snapshots`` is newest-first
  // so the resulting Map iteration order is also newest-first; each
  // entry's ``idx`` is translated into the chronological array's
  // index before being handed to DayCard.
  const grouped = useMemo(() => {
    const map = new Map();
    snapshots.forEach((s, idx) => {
      const key = _mondayOf(s.window_end);
      if (!map.has(key)) map.set(key, []);
      const chronoIdx = chronoSnapshots.length - 1 - idx;
      map.get(key).push({ snapshot: s, idx: chronoIdx });
    });
    return map;
  }, [snapshots, chronoSnapshots]);

  const goDetail = (date) => navigate(`/history/${date}`);

  return (
    <div style={{
      minHeight: "100vh",
      background: VELLUM,
      padding: "max(20px, calc(env(safe-area-inset-top) + 12px)) 16px var(--safe-bottom)",
      boxSizing: "border-box",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 24,
      }}>
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          style={{
            all: "unset", cursor: "pointer",
            fontSize: 20, color: ANTIQUE_GOLD, minWidth: 28,
          }}
        >
          ←
        </button>
        <span style={{
          fontSize: 11, fontWeight: 500, color: ANTIQUE_GOLD,
          letterSpacing: "0.08em",
        }}>
          SIGNAL HISTORY
        </span>
      </div>

      {error && (
        <p style={{ fontSize: 13, color: WARM_BLACK }}>{error}</p>
      )}

      {loading && (
        <p style={{ fontSize: 13, color: TEXT_SECONDARY }}>Loading…</p>
      )}

      {!loading && !error && snapshots.length === 0 && (
        <p style={{ fontSize: 13, color: TEXT_SECONDARY }}>
          You don't have any check-ins yet. Complete one and your history will
          appear here.
        </p>
      )}

      {/* Weekly groups */}
      {!loading && Array.from(grouped.entries()).map(([weekKey, entries]) => (
        <section key={weekKey} style={{ marginBottom: 24 }}>
          <div style={{
            display: "flex", justifyContent: "flex-end",
            marginBottom: 12,
          }}>
            <span style={{
              fontSize: 12, color: ANTIQUE_GOLD,
            }}>
              {_weekHeader(weekKey)}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map(({ snapshot, idx }) => (
              <DayCard
                key={snapshot.window_end}
                snapshot={snapshot}
                idx={idx}
                snapshotsAll={chronoSnapshots}
                isToday={snapshot.window_end === today}
                hasJournal={journalDates.has(snapshot.window_end)}
                onTap={goDetail}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Sentinel for infinite scroll + pagination state */}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {loadingMore && (
        <p style={{
          textAlign: "center", fontSize: 12, color: TEXT_SECONDARY,
          marginTop: 12,
        }}>
          Loading older weeks…
        </p>
      )}
      {!loading && !loadingMore && !hasMore && snapshots.length > 0 && (
        <p style={{
          textAlign: "center", fontSize: 12, color: TEXT_SECONDARY,
          marginTop: 12,
        }}>
          You've reached the beginning of your history.
        </p>
      )}
    </div>
  );
}
